import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { State } from "../redux/Types";
import { userSlice } from "@/redux/User";
import environment from "../constants/Environment";
import useGetPlanDetails from "@/hooks/useGetPlanDetails";
import { useQueryClient } from "@tanstack/react-query";
import useOpenaiConfig from "@/pages/omni-builder/react-query/configs/useOpenaiConfig";
import useGetCredits from "@/hooks/useGetCredits";

const environmentData = environment();

export default function AuthGuard({ children }) {
  const router = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: State) => state.user.isLoggedIn);
  const helloBarDelay = useSelector((state: State) => state.user.helloBarDelay);
  const queryClient = useQueryClient();
  const queryCache = queryClient.getQueryCache();
  const planDetails = useGetPlanDetails();

  const aiKeysConfigs = useOpenaiConfig();
  const userName = useSelector((state: State) => state.user.userName);
  const userEmail = useSelector((state: State) => state.user.userEmail);
  const hasSetPassword = useSelector(
    (state: State) => state.user?.hasSetPassword ?? true,
  );
  const companyModel = useSelector((state: State) => state.user.companyModel);
  useGetCredits();
  useEffect(() => {
    if (!isLoggedIn) {
      queryCache.clear();
      router("/login");
    }
  }, [isLoggedIn, queryCache, router]);

  useEffect(() => {
    if (
      planDetails.data?.plan?.trial ||
      planDetails.data?.plan?.trial === null ||
      planDetails.data?.plan?.name === "Lifetime"
    ) {
      if (helloBarDelay) {
        setTimeout(
          () => {
            dispatch(userSlice.actions.setShowHelloBar(true));
            dispatch(userSlice.actions.setHelloBarDelay(false));
          },
          10 * 60 * 1000,
        );
      } else {
        dispatch(userSlice.actions.setShowHelloBar(true));
      }
    }
  }, [planDetails.data?.plan, helloBarDelay]);

  useEffect(() => {
    if (isLoggedIn && !hasSetPassword) {
      router("/newpassword");
    }
  }, [isLoggedIn, hasSetPassword]);

  useEffect(() => {
    if (planDetails.isSuccess) {
      if (
        planDetails.data?.plan?.status === "expired" ||
        planDetails.data?.plan?.status === "cancelled"
      ) {
        if (
          planDetails.data?.plan?.plan_code === environmentData.TRIALPLAN ||
          planDetails.data?.plan?.name === "Lifetime"
        ) {
          router("/trial_expired");
        } else {
          router("/plan_expired");
        }
      }

      if (environmentData.isAI) {
        window.ChatbotBuilder?.setUserData({
          name: userName,
          email: userEmail,
          plan: planDetails.data?.plan?.name ?? "",
          plan_status: planDetails.data?.plan?.status ?? "",
          isLoggedIn: "true",
          aiModel: companyModel,
        });
      }
    } else if (planDetails.isError) {
      console.log(planDetails.error);
    }
  }, [planDetails.data]);

  useEffect(() => {
    if (environmentData.isAI && aiKeysConfigs.data) {
      window.ChatbotBuilder?.setUserData({
        apiKeyAdded: aiKeysConfigs.data.length ? "true" : "false",
      });
    }
  }, [aiKeysConfigs.data]);

  return <>{children}</>;
}
